import React, { useRef, useMemo, useEffect, useCallback, useState, Component } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../api/api';
import { Box } from '@mui/material';
import CustomToolbar from './CustomToolBar';
import ImageResize from 'quill-image-resize';
import { useError } from '../../contexts/ErrorContext';
import imageCompression from 'browser-image-compression';
import { installFeedLinks } from './feedEditorLinks';

const SERVER_IMAGE_LIMIT = 5 * 1024 * 1024;
const EDITOR_SOURCE_IMAGE_LIMIT = 10 * 1024 * 1024;

// ReactQuill을 감싸는 클래스 컴포넌트 (findDOMNode 경고 우회)
class QuillWrapper extends Component {
  getEditor() {
    return this.reactQuillRef?.getEditor();
  }

  render() {
    return <ReactQuill ref={(el) => (this.reactQuillRef = el)} {...this.props} />;
  }
}

// 폰트 사이즈 등록
const Size = Quill.import('attributors/style/size');
Size.whitelist = [
  '9px',
  '10px',
  '11px',
  '12px',
  '13px',
  '14px',
  '15px',
  '16px',
  '18px',
  '20px',
  '24px',
  '30px',
  '40px',
  '60px',
];
Quill.register(Size, true);

// 이미지 리사이징
Quill.register('modules/ImageResize', ImageResize);

let editorInstanceSequence = 0;

const ReactQuillEditor = ({
  value,
  onChange,
  feedLinks = false,
  toolbarTop,
  readOnly = false,
  onUploadPendingChange,
}) => {
  const quillRef = useRef(null);
  const session = useRef({ live: true });
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const uploadCallback = useRef(onUploadPendingChange);
  uploadCallback.current = onUploadPendingChange;
  const pickers = useRef(new Set());
  useEffect(() => {
    const lifetime = session.current;
    const inputs = pickers.current;
    lifetime.live = true;
    return () => {
      lifetime.live = false;
      inputs.forEach((input) => input.remove());
      inputs.clear();
    };
  }, []);
  const toolbarIdRef = useRef(null);
  if (!toolbarIdRef.current) {
    editorInstanceSequence += 1;
    toolbarIdRef.current = `toolbar-${editorInstanceSequence}`;
  }
  const toolbarId = toolbarIdRef.current;
  const { showError } = useError();
  const [imageUploadPending, setImageUploadPending] = useState(false);
  const imageUploadPendingRef = useRef(false);
  const lastSelectionRef = useRef(null);
  const normalizationRef = useRef(null);
  const handleChange = (html, delta, source, editor) => {
    if (readOnlyRef.current || !session.current.live) return;
    if (source !== 'user') {
      // Mounting/loading Quill canonicalizes tags, styles and link attributes.
      // It is not an edit. Keep the original value so an untouched form closes
      // cleanly, including switching languages and reopening an existing post.
      normalizationRef.current = { original: value, normalized: html };
      return;
    }
    const normalized = normalizationRef.current;
    // Undo/reverting to the loaded document must also return to the original
    // draft baseline even when Quill serialized that document differently.
    onChange(normalized?.normalized === html ? normalized.original : html, delta, source, editor);
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!feedLinks || !editor) return;
    return installFeedLinks(editor);
  }, [feedLinks]);

  const handleImageUpload = useCallback(async () => {
    if (imageUploadPendingRef.current || readOnlyRef.current || !session.current.live) return;
    const editor = quillRef.current?.getEditor();
    const currentSelection = editor?.getSelection();
    if (currentSelection) lastSelectionRef.current = currentSelection;

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    pickers.current.add(input);
    const removeInput = () => {
      input.remove();
      pickers.current.delete(input);
    };

    input.addEventListener(
      'change',
      async () => {
        if (!session.current.live || readOnlyRef.current || imageUploadPendingRef.current) {
          removeInput();
          return;
        }
        const file = input.files?.[0];
        if (!file) {
          removeInput();
          return;
        }

        const extensionLooksLikeImage = /\.(avif|gif|heic|heif|jpe?g|png|webp)$/i.test(file.name);
        if (!file.type.startsWith('image/') && !extensionLooksLikeImage) {
          showError('이미지 파일만 선택할 수 있습니다.');
          removeInput();
          return;
        }
        if (file.size > EDITOR_SOURCE_IMAGE_LIMIT) {
          showError('원본 이미지는 최대 10MB까지 선택할 수 있습니다.');
          removeInput();
          return;
        }
        const isAnimatedGif = file.type === 'image/gif' || /\.gif$/i.test(file.name);
        if (isAnimatedGif && file.size > SERVER_IMAGE_LIMIT) {
          showError('움직이는 GIF는 애니메이션 보존을 위해 5MB 이하만 업로드할 수 있습니다.');
          removeInput();
          return;
        }

        imageUploadPendingRef.current = true;
        setImageUploadPending(true);
        uploadCallback.current?.(true);
        try {
          const preparedFile =
            file.size > SERVER_IMAGE_LIMIT
              ? await imageCompression(file, {
                  maxSizeMB: 4.5,
                  maxWidthOrHeight: 2560,
                  useWebWorker: true,
                  preserveExif: false,
                })
              : file;
          if (!session.current.live || readOnlyRef.current) return;
          if (preparedFile.size > SERVER_IMAGE_LIMIT) {
            throw new Error('compressed-image-still-too-large');
          }

          const formData = new FormData();
          formData.append('image', preparedFile, preparedFile.name || file.name);
          const { data: response } = await api.postAuth('/images/upload', formData);
          if (!session.current.live || readOnlyRef.current) return;
          const imageUrl = response.data.url;

          const activeEditor = quillRef.current?.getEditor();
          if (!activeEditor || !imageUrl) throw new Error('missing-editor-or-image-url');
          const range = activeEditor.getSelection() || lastSelectionRef.current;
          const index = Math.min(
            range?.index ?? activeEditor.getLength(),
            activeEditor.getLength(),
          );
          activeEditor.insertEmbed(index, 'image', imageUrl, 'user');
          activeEditor.setSelection(index + 1, 0, 'silent');
        } catch (error) {
          if (!session.current.live) return;
          console.error('이미지 업로드 실패:', error.message);
          showError(
            file.size > SERVER_IMAGE_LIMIT
              ? '이미지를 5MB 이하로 최적화하지 못했습니다. 더 작은 이미지를 선택해주세요.'
              : '이미지 업로드에 실패했습니다. 다시 시도해주세요.',
          );
        } finally {
          imageUploadPendingRef.current = false;
          if (session.current.live) {
            setImageUploadPending(false);
            uploadCallback.current?.(false);
          }
          removeInput();
        }
      },
      { once: true },
    );
    input.addEventListener('cancel', removeInput, { once: true });

    // iOS Safari can dispatch change immediately after the picker closes, so
    // the listener and a live DOM node must exist before opening it.
    input.click();
  }, [showError]);

  // Quill의 toolbar 이벤트 리스너는 커스텀 핸들러 실행 전에 항상 quill.focus()를 호출함.
  // focus() → selection.focus() → root.focus() 체인에서 preventScroll 없이 실행되어
  // 브라우저가 window를 스크롤하는 문제가 발생하므로, root.focus를 패치해 방지.
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const root = quill.root;
    const originalFocus = root.focus.bind(root);
    root.focus = (options) => originalFocus({ ...options, preventScroll: true });

    return () => {
      root.focus = originalFocus;
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // 현재 커서 위치의 포맷에 맞게 툴바 업데이트
    const updateToolbar = () => {
      // Quill의 포맷 적용이 완료된 후 툴바 업데이트
      setTimeout(() => {
        const range = quill.getSelection();
        if (!range) return;
        lastSelectionRef.current = range;

        const format = quill.getFormat(range);
        const sizeSelect = document.querySelector(`#${toolbarId} .ql-size`);

        if (sizeSelect) {
          // 현재 포맷의 size가 있으면 그 값으로, 없으면 기본값(14px)으로 설정
          sizeSelect.value = format.size || '14px';
        }
      }, 0);
    };

    // selection-change와 text-change 이벤트 리스너 등록
    quill.on('selection-change', updateToolbar);
    quill.on('text-change', updateToolbar);

    // 초기 툴바 상태 설정
    updateToolbar();

    // 클린업
    return () => {
      quill.off('selection-change', updateToolbar);
      quill.off('text-change', updateToolbar);
    };
  }, [toolbarId]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          image: handleImageUpload,
        },
      },
      ImageResize: {
        parchment: Quill.import('parchment'),
      },
    }),
    [handleImageUpload, toolbarId],
  );

  return (
    <Box
      inert={readOnly ? '' : undefined}
      sx={{
        flex: 1,
        '& .ql-container': {
          height: 'auto',
          minHeight: '320px',
          borderTop: 'none',
        },
        '& .ql-editor': {
          fontSize: '14px', // 기본 폰트 크기 설정
          // The editable element, not just its container, must cover the empty
          // feed body so a click anywhere there places the initial caret.
          ...(feedLinks ? { minHeight: '320px' } : {}),
        },
        '& .ql-editor.ql-blank::before': {
          fontStyle: 'normal', // placeholder의 기본 이탤릭 스타일 제거
        },
        // 색상 팔레트를 왼쪽으로 이동
        '& .ql-color .ql-picker-options': {
          left: '-65px !important',
          right: 'auto !important',
        },
        minWidth: feedLinks ? 0 : '320px',
      }}
    >
      <CustomToolbar toolbarId={toolbarId} stickyTop={toolbarTop} />
      {imageUploadPending ? (
        <Box
          aria-live="polite"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            px: 1.5,
            py: 0.75,
            bgcolor: 'rgba(255,255,255,0.94)',
            color: 'text.secondary',
            fontSize: '12px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          이미지 처리·업로드 중…
        </Box>
      ) : null}
      <QuillWrapper
        readOnly={readOnly}
        theme="snow"
        ref={quillRef}
        value={
          normalizationRef.current?.original === value ? normalizationRef.current.normalized : value
        }
        onChange={handleChange}
        modules={modules}
        formats={[
          'size',
          'bold',
          'italic',
          'underline',
          'strike',
          'color',
          'align',
          'link',
          'image',
          'float',
          'height',
          'width',
        ]}
        placeholder={'설명을 입력하세요.'}
      />
    </Box>
  );
};

export default ReactQuillEditor;
